import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { alpha, Box, Card, Typography, useTheme } from '@mui/material';

import { USER_APPROVE_STATUS, USER_QUALIFY_STATUS } from '@/config/constants';
import { UserLoanStatusChips } from '@/components/Profile/UserLoanStatusChips';
import type { ProgressCheckItem } from '@/utils/portfolioProgress';
import { getproductOwner } from '@/utils/product';
import { Product } from '@/types/ProductType';

export function buildProductProgressItems(
  product: Product
): ProgressCheckItem[] {
  const user = getproductOwner(product);
  return [
    {
      label: 'Add a home to Unbeatable',
      value: true,
    },
    {
      label: 'Complete a budget for guided shopping',
      value: Boolean(user?.loanInfo && product.loanInfo.zipCode),
    },
    {
      label: 'Check how much you can qualify for',
      value: user?.qualifyStatus === USER_QUALIFY_STATUS.QUALIFY,
    },
    {
      label: 'Receive a personal loan estimate',
      value: Boolean(user?.approveStatus === USER_APPROVE_STATUS.APPROVED),
    },
    {
      label: 'Make an offer to buy a property',
      value: Boolean(product.contract),
    },
  ];
}

type ProductDetailProgressProps = {
  product?: Product;
  /** Portfolio-wide checklist; when set, `product` is only needed for legacy footers. */
  checkItems?: ProgressCheckItem[];
  /** Rendered inside the progress card below the checklist (e.g. Continue mortgage file). */
  footer?: ReactNode;
  /** When false, omits top margin (e.g. portfolio grid above property cards). */
  showTopMargin?: boolean;
  /** Stretch to fill a grid cell beside property cards. */
  fillHeight?: boolean;
  /** Tighter spacing for portfolio grid layout. */
  compact?: boolean;
};

export const ProductDetailProgress = ({
  product,
  checkItems: checkItemsProp,
  footer,
  showTopMargin = true,
  fillHeight = false,
  compact = false,
}: ProductDetailProgressProps) => {
  const theme = useTheme();

  const checkItems = useMemo(() => {
    if (checkItemsProp) return checkItemsProp;
    if (!product) return [];
    return buildProductProgressItems(product);
  }, [checkItemsProp, product]);

  return (
    <Card
      sx={{
        p: compact ? 1.5 : 2,
        mt: showTopMargin ? 2 : 0,
        border: `1px solid ${theme.palette.greyShades.border1}`,
        borderRadius: compact ? '12px' : 4,
        boxShadow: fillHeight
          ? 'none'
          : `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
        background: alpha(theme.palette.primary.main, 0.03),
        color: theme.palette.text.primary,
        ...(fillHeight
          ? {
              flex: 1,
              minHeight: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }
          : {}),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: compact ? 0.5 : 1,
        }}
      >
        <UserLoanStatusChips />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: compact ? 1 : 2,
          ...(fillHeight ? { flex: 1, minHeight: 0, overflow: 'auto' } : {}),
        }}
      >
        {checkItems.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: compact ? 1 : 2,
            }}
          >
            {item.value ? (
              <i className="fa-light fa-check color-success" />
            ) : (
              <i className="fa-light fa-close color-error" />
            )}
            <Typography
              sx={{ color: theme.palette.greyShades.grey1 }}
              variant={compact ? 'caption' : 'body2'}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
      {footer ? (
        <Box
          sx={{
            mt: compact ? 1.5 : 2,
            pt: compact ? 1.5 : 2,
            borderTop: `1px solid ${theme.palette.greyShades.border1}`,
            flexShrink: 0,
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Card>
  );
};
